import React from "react";
import {
    DynamicTable,
    customColumnProps,
} from "@maherunlocker/custom-react-table";

import { EtkColumn, EtkTableProps } from "./types";

/**
 * Thin wrapper around `@maherunlocker/custom-react-table`'s `DynamicTable`.
 *
 * - Same look & feel as the legacy table (no custom styling on top).
 * - Cleaner prop surface: `customColumns` instead of `arrayOfCustomColumns`,
 *   `onSelectionChange` instead of `setSelectedRows`, etc.
 * - Use it when you want the standard ETK fidelity-style table without
 *   re-typing 15+ props at every call site.
 */
const renderEmptyAction = () => <></>;

function toCustomColumnProps(col: EtkColumn): customColumnProps {
    return {
        indexOFColumn: col.index,
        columnName: col.header,
        customJsx: col.cell,
        filterName: col.filterName ?? col.header,
        canFilter: col.filterable,
        disableFilter: col.disableFilter,
    } as customColumnProps;
}

export function EtkTable(props: EtkTableProps): JSX.Element {
    const {
        url,
        headers,
        customColumns,
        canExpand,
        canSelect = true,
        customSelect = true,
        onSelectionChange,
        onDataChange,
        toolbarRight,
        refreshKey,
        dataIsUpdated,
        setDataIsUpdated,
        showSearch = true,
        showFilters,
        canResize = true,
        canMovedCheckboxLeftOnExpand = true,
        filterActive,
        setLocalFilterActive,
        name,
        minHeight,
        maxHeight,
    } = props;

    const arrayOfCustomColumns = React.useMemo<customColumnProps[] | undefined>(
        () => customColumns?.map(toCustomColumnProps),
        [customColumns]
    );

    return (
        <DynamicTable
            key={refreshKey !== undefined ? String(refreshKey) : undefined}
            url={url}
            requestHeader={headers}
            arrayOfCustomColumns={arrayOfCustomColumns}
            actionColumn={renderEmptyAction}
            canExpand={canExpand}
            canSelect={canSelect}
            customSelect={customSelect}
            setSelectedRows={
                onSelectionChange as
                    | React.Dispatch<React.SetStateAction<any[]>>
                    | undefined
            }
            setData={onDataChange}
            customJsxSideFilterButton={toolbarRight}
            showGlobalFilter={showSearch}
            showFilter={showFilters}
            canResize={canResize}
            canMovedCheckboxLeftOnExpand={canMovedCheckboxLeftOnExpand}
            filterActive={filterActive}
            setLocalFilterActive={
                setLocalFilterActive as
                    | React.Dispatch<React.SetStateAction<boolean>>
                    | undefined
            }
            dataIsUpdated={dataIsUpdated}
            setDataIsUpdated={
                setDataIsUpdated as
                    | React.Dispatch<
                          React.SetStateAction<number | boolean>
                      >
                    | undefined
            }
            elevationTable={1}
            minHeight={minHeight}
            maxHeight={maxHeight}
            name={name}
        />
    );
}
